import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Fuel, Leaf, TrendingDown, Loader2, Mail, Check, ArrowRight } from "lucide-react";
import { getVehicles, submitLead } from "../lib/api";
import { fmtMoney } from "../config";
import { GAS_VEHICLES, EVS, MAKES, EV_MAKES } from "../lib/vehicleCatalog";

/**
 * Defaults are conservative, Ohio-grounded:
 *  - Residential rate: $0.14/kWh (AEP Ohio Columbus area average)
 *  - DC fast-charge rate: $0.45/kWh (EVgo/Electrify America ballpark)
 *  - Gas: $3.20/gal (Ohio AAA avg, easy for Scott to tune)
 *  - Ohio grid CO2: ~1.1 lbs/kWh (gas + coal + nuclear mix)
 *  - Gasoline CO2: 19.6 lbs/gal
 *  - Maintenance savings vs gas: ~$650/yr (no oil, fewer brake jobs, but EV tires can cost a bit more)
 */
const STOCK_PHOTOS = {
  // Known-good Chevrolet press photos hosted on free CDNs (no auth/sign-up).
  // Match the EV class so we never show a Porsche for a Trax, etc.
  "Bolt EV":    "https://www.chevrolet.com/content/dam/chevrolet/na/us/english/index/vehicles/2023/cars/bolt-euv-redline-edition/01-images/2023-chevrolet-bolt-euv-redline-edition.jpg",
  "Equinox EV": "https://www.chevrolet.com/content/dam/chevrolet/na/us/english/index/vehicles/2025/suvs/equinox-ev/01-images/2025-chevrolet-equinox-ev-3rs-mediterranean-blue.jpg?imwidth=1200",
  "Blazer EV":  "https://www.chevrolet.com/content/dam/chevrolet/na/us/english/index/vehicles/2025/suvs/blazer-ev/01-images/2025-chevrolet-blazer-ev-radiant-red.jpg?imwidth=1200",
  "Silverado EV": "https://www.chevrolet.com/content/dam/chevrolet/na/us/english/index/vehicles/2025/trucks/silverado-ev/01-images/2025-chevrolet-silverado-ev-rst-cypress-gray.jpg?imwidth=1200",
  "Trax":       "https://www.chevrolet.com/content/dam/chevrolet/na/us/english/index/vehicles/2024/suvs/trax/01-images/2024-chevrolet-trax-2rs-cacti-green-front-quarter.jpg?imwidth=1200",
  "Malibu":     "https://www.chevrolet.com/content/dam/chevrolet/na/us/english/index/vehicles/2024/cars/malibu/01-images/2024-chevrolet-malibu-rs-mosaic-black.jpg?imwidth=1200",
  "Equinox":    "https://www.chevrolet.com/content/dam/chevrolet/na/us/english/index/vehicles/2025/suvs/equinox/01-images/2025-chevrolet-equinox-rs-riptide-blue.jpg?imwidth=1200",
  "Tahoe":      "https://www.chevrolet.com/content/dam/chevrolet/na/us/english/index/vehicles/2025/suvs/tahoe/01-images/2025-chevrolet-tahoe-z71-northsky-blue.jpg?imwidth=1200",
  "Silverado 1500": "https://www.chevrolet.com/content/dam/chevrolet/na/us/english/index/vehicles/2025/trucks/silverado-1500/01-images/2025-chevrolet-silverado-1500-lt-trail-boss-cherry-red.jpg?imwidth=1200",
};

function stockPhotoFor(modelName) {
  if (!modelName) return null;
  const key = Object.keys(STOCK_PHOTOS).find((k) => modelName.includes(k));
  return key ? STOCK_PHOTOS[key] : null;
}


const EV_CLASSES = {
  compact_car: {
    label: "Compact Car",
    ev_kwh_per_mi: 0.27,
    gas_mpg: 32,
    body_style: "car",
    ev_example: "Chevy Bolt EV",
    gas_example: "Chevy Malibu",
  },
  midsize_suv: {
    label: "Midsize SUV",
    ev_kwh_per_mi: 0.32,
    gas_mpg: 26,
    body_style: "suv",
    ev_example: "Chevy Equinox EV",
    gas_example: "Chevy Equinox",
  },
  fullsize_suv: {
    label: "Full-Size SUV",
    ev_kwh_per_mi: 0.40,
    gas_mpg: 18,
    body_style: "suv",
    ev_example: "Chevy Blazer EV",
    gas_example: "Chevy Tahoe",
  },
  truck: {
    label: "Truck",
    ev_kwh_per_mi: 0.45,
    gas_mpg: 19,
    body_style: "truck",
    ev_example: "Chevy Silverado EV",
    gas_example: "Chevy Silverado 1500",
  },
};

const ANNUAL_MAINT_SAVINGS = 650;
const OHIO_GRID_CO2_LBS_PER_KWH = 1.1;
const GAS_CO2_LBS_PER_GAL = 19.6;

function NumField({ label, value, onChange, suffix, testId, step = 1, min = 0 }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wide text-[#6b7c93]">{label}</span>
      <div className="mt-1 flex items-stretch rounded-lg border border-[#D9E1EA] bg-white overflow-hidden focus-within:border-[var(--ssc-blue)]">
        <input
          type="number"
          inputMode="decimal"
          step={step}
          min={min}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 py-2 text-base font-bold text-[var(--ssc-navy)] outline-none"
          data-testid={testId}
        />
        {suffix && (
          <span className="px-3 py-2 text-sm font-bold text-[#6b7c93] bg-[#F4F7FB] border-l border-[#D9E1EA]">
            {suffix}
          </span>
        )}
      </div>
    </label>
  );
}

function ResultStat({ icon: Icon, label, value, color = "var(--ssc-navy)", sub }) {
  return (
    <div className="rounded-2xl bg-white border border-[#D9E1EA] p-4">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#6b7c93]">
        <Icon size={14} /> {label}
      </div>
      <div className="mt-1 text-2xl md:text-3xl font-black" style={{ color }}>
        {value}
      </div>
      {sub && <div className="text-xs text-[#6b7c93] mt-1">{sub}</div>}
    </div>
  );
}

export default function EvCostCalculator() {
  // Generic "class" fallback for users who skip the detailed picker.
  const [evClassKey, setEvClassKey] = useState("midsize_suv");
  // Specific vehicle pickers — preferred path.
  const [gasMake, setGasMake] = useState("");
  const [gasVehicleId, setGasVehicleId] = useState("");
  const [evMake, setEvMake] = useState("");
  const [evVehicleId, setEvVehicleId] = useState("");

  const [monthlyMiles, setMonthlyMiles] = useState(1000);
  const [homeKwhRate, setHomeKwhRate] = useState(0.14);
  const [dcKwhRate, setDcKwhRate] = useState(0.45);
  const [homePct, setHomePct] = useState(90);
  const [gasPrice, setGasPrice] = useState(3.20);

  const gasModelsForMake = useMemo(() => GAS_VEHICLES.filter(v => v.make === gasMake), [gasMake]);
  const evModelsForMake = useMemo(() => EVS.filter(v => v.make === evMake), [evMake]);
  const selectedGas = GAS_VEHICLES.find(v => v.id === gasVehicleId);
  const selectedEv  = EVS.find(v => v.id === evVehicleId);

  const evClass = EV_CLASSES[evClassKey];

  // Effective per-mile efficiency — prefer the explicit picks, fall back to
  // the generic class so the calculator always produces a number.
  const gasMpg     = selectedGas?.mpg || evClass.gas_mpg;
  const evKwhPerMi = selectedEv ? selectedEv.kwh_per_100mi / 100 : evClass.ev_kwh_per_mi;
  const gasLabel   = selectedGas
    ? `${selectedGas.make} ${selectedGas.model} · ${selectedGas.engine}`
    : `${evClass.label} (generic)`;
  const evLabel    = selectedEv
    ? `${selectedEv.make} ${selectedEv.model}`
    : `${evClass.label} EV (generic)`;

  const result = useMemo(() => {
    const miles = Math.max(0, Number(monthlyMiles) || 0);
    const homeShare = Math.max(0, Math.min(100, Number(homePct) || 0)) / 100;
    const dcShare = 1 - homeShare;

    const evKwh = miles * evKwhPerMi;
    const evCostMonth = evKwh * homeShare * Number(homeKwhRate) + evKwh * dcShare * Number(dcKwhRate);

    const gasGallons = miles / gasMpg;
    const gasCostMonth = gasGallons * Number(gasPrice);

    const fuelSavingsMonth = gasCostMonth - evCostMonth;
    const fuelSavings5yr = fuelSavingsMonth * 60;
    const maintSavings5yr = ANNUAL_MAINT_SAVINGS * 5;
    const total5yr = fuelSavings5yr + maintSavings5yr;

    const co2GasMonth = gasGallons * GAS_CO2_LBS_PER_GAL;
    const co2EvMonth = evKwh * OHIO_GRID_CO2_LBS_PER_KWH;
    const co2SavedYear = (co2GasMonth - co2EvMonth) * 12;

    return {
      evCostMonth, gasCostMonth, fuelSavingsMonth, fuelSavings5yr,
      maintSavings5yr, total5yr, co2SavedYear, evKwh, gasGallons,
    };
  }, [gasMpg, evKwhPerMi, monthlyMiles, homeKwhRate, dcKwhRate, homePct, gasPrice]);

  // ---- Live inventory matches (best EV + best gas of same class) ----
  const [evMatch, setEvMatch] = useState(null);
  const [gasMatch, setGasMatch] = useState(null);
  const [loadingMatches, setLoadingMatches] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function fetchMatches() {
      setLoadingMatches(true);
      try {
        // Prefer the explicitly-picked vehicle for the inventory lookup so
        // we surface a Bolt-EV-vs-Malibu when the customer asked for that
        // exact comparison; fall back to the generic body-class otherwise.
        const evQuery = selectedEv
          ? { fuel_type: "electric", make: selectedEv.make, model: selectedEv.model, limit: 1, sort: "featured" }
          : { fuel_type: "electric", limit: 1, sort: "featured" };
        const gasQuery = selectedGas
          ? { fuel_type: "gas", make: selectedGas.make, model: selectedGas.model, limit: 1, sort: "featured" }
          : { fuel_type: "gas", body_style: evClass.body_style, limit: 1, sort: "featured" };
        const [evRes, gasRes] = await Promise.all([
          getVehicles(evQuery),
          getVehicles(gasQuery),
        ]);
        if (!cancelled) {
          setEvMatch(evRes?.items?.[0] || null);
          setGasMatch(gasRes?.items?.[0] || null);
        }
      } catch {
        // silent — calculator still works without matches
      } finally {
        if (!cancelled) setLoadingMatches(false);
      }
    }
    fetchMatches();
    return () => { cancelled = true; };
  }, [evClass.body_style, selectedEv, selectedGas]);

  // ---- Email-my-comparison lead capture ----
  const [showCapture, setShowCapture] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  async function sendComparison(e) {
    e.preventDefault();
    setSending(true);
    setErr("");
    const summary = [
      `Comparison: ${gasLabel} vs ${evLabel}`,
      `Monthly miles: ${monthlyMiles}`,
      `EV est. fuel: ${fmtMoney(result.evCostMonth)}/mo (${selectedEv ? selectedEv.kwh_per_100mi : Math.round(evClass.ev_kwh_per_mi*100)} kWh/100mi)`,
      `Gas est. fuel: ${fmtMoney(result.gasCostMonth)}/mo (${gasMpg} MPG)`,
      `Monthly savings: ${fmtMoney(result.fuelSavingsMonth)}`,
      `5-yr total savings (fuel + maintenance): ${fmtMoney(result.total5yr)}`,
      `CO2 avoided per year: ${Math.round(result.co2SavedYear).toLocaleString()} lbs`,
    ].join(" · ");
    try {
      await submitLead({
        name: name.trim() || "EV Calculator user",
        email: email.trim(),
        lead_type: "ev_help",
        lead_source: "EV Calculator",
        form_name: "EV Cost Calculator",
        message: summary,
        preferred_contact: "Email",
      });
      setSent(true);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Couldn't send right now. Try again in a moment.");
    } finally {
      setSending(false);
    }
  }

  const positiveSavings = result.fuelSavingsMonth > 0;

  return (
    <div className="rounded-3xl bg-gradient-to-br from-[#F4F7FB] to-[#E8F0FF] border border-[#D9E1EA] p-5 md:p-8" data-testid="ev-cost-calculator">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-12 h-12 rounded-2xl bg-[var(--ssc-blue)] text-white flex items-center justify-center">
          <Zap size={24} />
        </div>
        <div>
          <h3 className="text-2xl md:text-3xl font-black text-[var(--ssc-navy)] tracking-tight">
            EV vs Gas Cost Calculator
          </h3>
          <p className="text-[#475569] text-sm md:text-base mt-1">
            See your real monthly fuel cost, 5-year savings, and CO₂ avoided — built on Central Ohio rates.
          </p>
        </div>
      </div>

      {/* ---- Pick your exact vehicles ---- */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* GAS PICKER */}
        <div className="rounded-2xl bg-white border border-[#D9E1EA] p-4" data-testid="ev-calc-gas-picker">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[var(--ssc-navy)] mb-2">
            <Fuel size={14} /> Your current gas vehicle
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block col-span-1">
              <span className="text-[10px] font-bold uppercase tracking-wide text-[#6b7c93]">Make</span>
              <select value={gasMake}
                onChange={(e) => { setGasMake(e.target.value); setGasVehicleId(""); }}
                className="mt-1 w-full px-2 py-1.5 rounded border border-[#D9E1EA] text-sm font-bold text-[var(--ssc-navy)]"
                data-testid="ev-calc-gas-make">
                <option value="">— Make —</option>
                {MAKES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label className="block col-span-1">
              <span className="text-[10px] font-bold uppercase tracking-wide text-[#6b7c93]">Model + engine</span>
              <select value={gasVehicleId} onChange={(e) => setGasVehicleId(e.target.value)}
                disabled={!gasMake}
                className="mt-1 w-full px-2 py-1.5 rounded border border-[#D9E1EA] text-sm font-bold text-[var(--ssc-navy)] disabled:bg-[#F4F7FB]"
                data-testid="ev-calc-gas-model">
                <option value="">— Pick model —</option>
                {gasModelsForMake.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.model} · {v.engine} · {v.transmission} · {v.drivetrain}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {selectedGas ? (
            <div className="mt-2 text-xs text-[#475569]" data-testid="ev-calc-gas-detail">
              <strong className="text-[var(--ssc-navy)]">{selectedGas.years}</strong> · trims: {selectedGas.trim} · <strong className="text-emerald-700">{selectedGas.mpg} MPG combined</strong> <span className="text-[#9aa7b3]">(EPA est.)</span>
            </div>
          ) : (
            <div className="mt-2 text-xs text-[#6b7c93]">Pick your make &amp; model so we use real EPA MPG, not a generic average.</div>
          )}
        </div>

        {/* EV PICKER */}
        <div className="rounded-2xl bg-white border border-[#D9E1EA] p-4" data-testid="ev-calc-ev-picker">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[var(--ssc-blue)] mb-2">
            <Zap size={14} /> EV you're considering
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block col-span-1">
              <span className="text-[10px] font-bold uppercase tracking-wide text-[#6b7c93]">Make</span>
              <select value={evMake}
                onChange={(e) => { setEvMake(e.target.value); setEvVehicleId(""); }}
                className="mt-1 w-full px-2 py-1.5 rounded border border-[#D9E1EA] text-sm font-bold text-[var(--ssc-navy)]"
                data-testid="ev-calc-ev-make">
                <option value="">— Make —</option>
                {EV_MAKES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label className="block col-span-1">
              <span className="text-[10px] font-bold uppercase tracking-wide text-[#6b7c93]">Model + motor</span>
              <select value={evVehicleId} onChange={(e) => setEvVehicleId(e.target.value)}
                disabled={!evMake}
                className="mt-1 w-full px-2 py-1.5 rounded border border-[#D9E1EA] text-sm font-bold text-[var(--ssc-navy)] disabled:bg-[#F4F7FB]"
                data-testid="ev-calc-ev-model">
                <option value="">— Pick model —</option>
                {evModelsForMake.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.model} · {v.motor} · {v.drivetrain}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {selectedEv ? (
            <div className="mt-2 text-xs text-[#475569]" data-testid="ev-calc-ev-detail">
              <strong className="text-[var(--ssc-navy)]">{selectedEv.years}</strong> · trims: {selectedEv.trim} · transmission: {selectedEv.transmission} · <strong className="text-emerald-700">{selectedEv.kwh_per_100mi} kWh / 100 mi</strong> <span className="text-[#9aa7b3]">(EPA est.)</span>
            </div>
          ) : (
            <div className="mt-2 text-xs text-[#6b7c93]">Pick the exact EV — Bolt and Silverado EV burn very different kWh.</div>
          )}
        </div>
      </div>

      {/* Quick-class fallback for users who don't see their exact vehicle */}
      <details className="mt-3 rounded-lg bg-white/60 border border-[#D9E1EA] p-3 text-xs" data-testid="ev-calc-class-fallback">
        <summary className="cursor-pointer text-[var(--ssc-blue)] font-bold">Don't see your exact vehicle? Use a generic class →</summary>
        <div className="mt-2 flex items-center gap-2">
          <select value={evClassKey} onChange={(e) => setEvClassKey(e.target.value)}
            className="px-2 py-1 rounded border border-[#D9E1EA] text-sm font-bold text-[var(--ssc-navy)]"
            data-testid="ev-calc-class">
            {Object.entries(EV_CLASSES).map(([k, v]) => (
              <option key={k} value={k}>{v.label} — {v.gas_mpg} MPG vs {(v.ev_kwh_per_mi*100).toFixed(0)} kWh/100mi</option>
            ))}
          </select>
          <span className="text-[#6b7c93]">applied when no specific model is picked</span>
        </div>
      </details>

      {/* Driving + rates */}
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <NumField label="Monthly miles" value={monthlyMiles} onChange={setMonthlyMiles} suffix="mi" testId="ev-calc-miles" step={50} />
        <NumField label="Gas price" value={gasPrice} onChange={setGasPrice} suffix="$/gal" testId="ev-calc-gas-price" step={0.05} />
        <NumField label="Home charging" value={homeKwhRate} onChange={setHomeKwhRate} suffix="$/kWh" testId="ev-calc-home-rate" step={0.01} />
        <NumField label="DC fast-charge" value={dcKwhRate} onChange={setDcKwhRate} suffix="$/kWh" testId="ev-calc-dc-rate" step={0.01} />
        <NumField label="% charged at home" value={homePct} onChange={setHomePct} suffix="%" testId="ev-calc-home-pct" />
      </div>

      {/* Results */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <ResultStat icon={Zap} label="EV fuel / month" value={fmtMoney(result.evCostMonth)} color="var(--ssc-blue)" sub={`${Math.round(result.evKwh)} kWh · ${evLabel}`} />
        <ResultStat icon={Fuel} label="Gas fuel / month" value={fmtMoney(result.gasCostMonth)} sub={`${result.gasGallons.toFixed(1)} gal · ${gasLabel}`} />
        <ResultStat
          icon={TrendingDown}
          label="Monthly savings"
          value={fmtMoney(Math.max(0, result.fuelSavingsMonth))}
          color={positiveSavings ? "#10a37f" : "var(--ssc-navy)"}
          sub={positiveSavings ? "Switching to EV" : "Gas is cheaper at these inputs"}
        />
        <ResultStat
          icon={Leaf}
          label="CO₂ avoided / year"
          value={`${Math.max(0, Math.round(result.co2SavedYear)).toLocaleString()} lbs`}
          color="#10a37f"
        />
      </div>

      {/* Bottom line */}
      {positiveSavings && (
        <div className="mt-5 rounded-2xl bg-[var(--ssc-navy)] text-white p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid="ev-calc-5yr">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-[#9bb7ff]">5-year total savings</div>
            <div className="text-3xl md:text-4xl font-black mt-1">
              {fmtMoney(result.total5yr)}
            </div>
            <div className="text-[#cdd9ef] text-sm mt-1">
              {fmtMoney(result.fuelSavings5yr)} fuel + {fmtMoney(result.maintSavings5yr)} maintenance over 60 months
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowCapture(true)}
            className="ssc-btn ssc-btn-primary"
            data-testid="ev-calc-email-cta"
          >
            <Mail size={16} /> Email me this comparison
          </button>
        </div>
      )}

      {/* Inventory matches */}
      <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="ev-calc-matches">
        <MatchCard
          tone="ev"
          title={`Your ${evClass.label} EV match`}
          fallbackName={evClass.ev_example}
          vehicle={evMatch}
          loading={loadingMatches}
          shopHref={`/new-vehicles/evs`}
        />
        <MatchCard
          tone="gas"
          title={`Gas equivalent`}
          fallbackName={evClass.gas_example}
          vehicle={gasMatch}
          loading={loadingMatches}
          shopHref={`/new-vehicles`}
        />
      </div>

      {/* Email capture inline form */}
      {showCapture && !sent && (
        <form onSubmit={sendComparison} className="mt-5 rounded-2xl bg-white border border-[#D9E1EA] p-4 md:p-5" data-testid="ev-calc-capture-form">
          <div className="font-extrabold text-[var(--ssc-navy)]">Send this comparison to my inbox</div>
          <p className="text-sm text-[#6b7c93] mt-1">Scott will follow up only if you ask — no spam.</p>
          <div className="mt-3 grid md:grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="px-3 py-2 rounded-lg border border-[#D9E1EA] outline-none focus:border-[var(--ssc-blue)]"
              data-testid="ev-calc-name"
            />
            <input
              type="email"
              required
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="px-3 py-2 rounded-lg border border-[#D9E1EA] outline-none focus:border-[var(--ssc-blue)]"
              data-testid="ev-calc-email"
            />
          </div>
          {err && <div className="mt-2 text-sm text-[var(--ssc-red)]">{err}</div>}
          <div className="mt-3 flex gap-2">
            <button type="submit" disabled={sending} className="ssc-btn ssc-btn-primary text-sm" data-testid="ev-calc-send">
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
              {sending ? "Sending…" : "Send to me"}
            </button>
            <button type="button" onClick={() => setShowCapture(false)} className="ssc-btn ssc-btn-secondary text-sm">
              Cancel
            </button>
          </div>
        </form>
      )}

      {sent && (
        <div className="mt-5 rounded-2xl bg-[#E6FFF4] border border-[#10a37f] p-4 flex items-center gap-3" data-testid="ev-calc-sent">
          <Check className="text-[#10a37f]" size={20} />
          <div>
            <div className="font-extrabold text-[var(--ssc-navy)]">Got it — Scott has your comparison.</div>
            <div className="text-sm text-[#475569]">Check your inbox shortly. You can keep tweaking the numbers above.</div>
          </div>
        </div>
      )}

      <p className="text-xs text-[#6b7c93] mt-5 leading-relaxed">
        Estimates only. Real EV cost varies by driving style, weather, charger speed, and utility plan. Maintenance savings reflect typical EV-vs-gas differences and are not a guarantee. Verify rates with your utility (AEP Ohio, etc.) and check current incentives at fueleconomy.gov.
      </p>
    </div>
  );
}

function MatchCard({ tone, title, fallbackName, vehicle, loading, shopHref }) {
  const isEv = tone === "ev";
  const accent = isEv ? "var(--ssc-blue)" : "var(--ssc-navy)";
  return (
    <div className="rounded-2xl bg-white border border-[#D9E1EA] overflow-hidden flex flex-col" data-testid={`ev-calc-match-${tone}`}>
      <div className="p-4 flex items-center gap-2 border-b border-[#EEF2F7]">
        {isEv ? <Zap size={16} style={{ color: accent }} /> : <Fuel size={16} style={{ color: accent }} />}
        <div className="text-xs font-bold uppercase tracking-wide" style={{ color: accent }}>{title}</div>
      </div>
      {loading ? (
        <div className="p-5 flex items-center gap-2 text-[#6b7c93]">
          <Loader2 size={16} className="animate-spin" /> Finding a match…
        </div>
      ) : vehicle ? (
        <div className="p-4 flex flex-col gap-2">
          {(() => {
            // Always prefer a known-good stock photo for the model — protects
            // against the inventory feed serving a wrong/Porsche image.
            const modelName = `${vehicle.make || ""} ${vehicle.model || ""}`.trim();
            const stock = stockPhotoFor(modelName);
            const imgUrl = stock || vehicle.photos?.[0]?.url;
            return imgUrl ? (
              <img
                src={imgUrl}
                alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                className="w-full h-36 object-cover rounded-lg"
                loading="lazy"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            ) : null;
          })()}
          <div className="font-extrabold text-[var(--ssc-navy)]">
            {vehicle.year} {vehicle.make} {vehicle.model} <span className="text-[#6b7c93] font-bold">{vehicle.trim}</span>
          </div>
          <div className="text-sm text-[#6b7c93]">
            {fmtMoney(vehicle.price)}{vehicle.mileage ? ` · ${Number(vehicle.mileage).toLocaleString()} mi` : ""}
            {vehicle.ev_range ? ` · ${vehicle.ev_range} mi range` : ""}
          </div>
          <Link
            to={`/vehicle/${vehicle.slug}`}
            className="ssc-btn ssc-btn-secondary text-sm self-start mt-1"
            data-testid={`ev-calc-match-${tone}-view`}
          >
            View this {isEv ? "EV" : "gas"} <ArrowRight size={14} />
          </Link>
        </div>
      ) : (
        <div className="p-4 flex flex-col gap-2">
          <div className="font-extrabold text-[var(--ssc-navy)]">{fallbackName}</div>
          <div className="text-sm text-[#6b7c93]">
            Not in stock right now — Scott can find one for you.
          </div>
          <Link
            to={shopHref}
            className="ssc-btn ssc-btn-secondary text-sm self-start mt-1"
            data-testid={`ev-calc-match-${tone}-shop`}
          >
            Browse inventory <ArrowRight size={14} />
          </Link>
        </div>
      )}
    </div>
  );
}
